import React, { useState, useEffect } from 'react';

/**
 * IzmexCircuit - B2B Marketplace Flow
 * 
 * Multi-vendor catalog to buyer discovery pipeline.
 * Shows: CATALOG → SEARCH → MATCH → RFQ → LEAD
 */

export function IzmexCircuit({ className = '' }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div 
      className={`relative ${className} transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      style={{ width: '260px', height: '180px' }}
    >
      <svg 
        viewBox="0 0 130 90" 
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <filter id="izmexGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <pattern id="izmexGrid" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(15, 118, 110, 0.08)" strokeWidth="0.3"/>
          </pattern>
        </defs>

        {/* Background grid */}
        <rect width="130" height="90" fill="url(#izmexGrid)" />

        {/* Connection paths */}
        <g className="connections">
          {/* Vendor inputs merging into catalog */}
          <path d="M 12 20 L 22 20 L 30 35" fill="none" stroke="rgba(15, 118, 110, 0.35)" strokeWidth="0.5" />
          <path d="M 12 40 L 30 40" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <path d="M 12 60 L 22 60 L 30 45" fill="none" stroke="rgba(15, 118, 110, 0.35)" strokeWidth="0.5" />
          
          {/* Catalog to Search */}
          <path d="M 45 40 L 52 40" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          
          {/* Search to Match */}
          <path d="M 68 40 L 78 40" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          
          {/* Match to RFQ/Lead */}
          <path d="M 94 35 L 107 28" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <path d="M 94 45 L 107 52" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          
          {/* Analytics feedback loop */}
          <path d="M 60 50 L 60 72 L 38 72 L 38 50" fill="none" stroke="rgba(15, 118, 110, 0.25)" strokeWidth="0.4" strokeDasharray="2,2" />
          
          {/* Recommendation feedback */}
          <path d="M 86 32 L 86 18 L 60 18 L 60 32" fill="none" stroke="rgba(15, 118, 110, 0.2)" strokeWidth="0.4" strokeDasharray="1.5,1.5" />
        </g>

        {/* Data pulse */}
        <g className="data-pulse">
          <circle r="1.5" fill="#0F766E" filter="url(#izmexGlow)">
            <animateMotion dur="5s" repeatCount="indefinite">
              <mpath href="#izmexPath" />
            </animateMotion>
            <animate attributeName="opacity" values="0;1;1;1;0" dur="5s" repeatCount="indefinite" />
          </circle>
          <path id="izmexPath" d="M 12 40 L 37 40 L 60 40 L 86 40 L 107 40" fill="none" stroke="none" />
        </g>

        {/* Nodes with labels */}
        <g className="nodes" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {/* Vendor Inputs */}
          <rect x="5" y="16" width="12" height="8" rx="1.5" fill="rgba(15, 118, 110, 0.06)" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <text x="11" y="22" textAnchor="middle" fill="#0F766E" fontSize="3">API</text>
          
          <rect x="5" y="36" width="12" height="8" rx="1.5" fill="rgba(15, 118, 110, 0.1)" stroke="rgba(15, 118, 110, 0.6)" strokeWidth="0.8" />
          <text x="11" y="42" textAnchor="middle" fill="#0F766E" fontSize="3" fontWeight="500">BULK</text>
          
          <rect x="5" y="56" width="12" height="8" rx="1.5" fill="rgba(15, 118, 110, 0.06)" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <text x="11" y="62" textAnchor="middle" fill="#0F766E" fontSize="3">IMG</text>
          
          {/* Catalog Engine */}
          <rect x="28" y="32" width="18" height="16" rx="1.5" fill="rgba(15, 118, 110, 0.08)" stroke="rgba(15, 118, 110, 0.5)" strokeWidth="0.7" />
          <text x="37" y="39" textAnchor="middle" fill="#0F766E" fontSize="4" fontWeight="500">CATALOG</text>
          <text x="37" y="45" textAnchor="middle" fill="rgba(15, 118, 110, 0.7)" fontSize="3">500K+</text>
          
          {/* Search (Core) */}
          <rect x="50" y="32" width="18" height="16" rx="2" fill="rgba(15, 118, 110, 0.15)" stroke="#0F766E" strokeWidth="1" />
          <text x="59" y="39" textAnchor="middle" fill="#0F766E" fontSize="4" fontWeight="600">SEARCH</text>
          <text x="59" y="45" textAnchor="middle" fill="rgba(15, 118, 110, 0.8)" fontSize="3">es</text>
          
          {/* Match Engine */}
          <rect x="78" y="32" width="18" height="16" rx="1.5" fill="rgba(15, 118, 110, 0.08)" stroke="rgba(15, 118, 110, 0.5)" strokeWidth="0.7" />
          <text x="87" y="39" textAnchor="middle" fill="#0F766E" fontSize="4" fontWeight="500">MATCH</text>
          <text x="87" y="45" textAnchor="middle" fill="rgba(15, 118, 110, 0.7)" fontSize="3">b2b</text>
          
          {/* RFQ Output */}
          <rect x="105" y="22" width="14" height="12" rx="1.5" fill="rgba(15, 118, 110, 0.06)" stroke="rgba(15, 118, 110, 0.45)" strokeWidth="0.6" />
          <text x="112" y="30" textAnchor="middle" fill="#0F766E" fontSize="3.5">RFQ</text>
          
          {/* Lead Output */}
          <rect x="105" y="46" width="14" height="12" rx="1.5" fill="rgba(15, 118, 110, 0.06)" stroke="rgba(15, 118, 110, 0.45)" strokeWidth="0.6" />
          <text x="112" y="54" textAnchor="middle" fill="#0F766E" fontSize="3.5">LEAD</text>
        </g>

        {/* Technical annotations */}
        <g className="annotations" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          <text x="59" y="56" textAnchor="middle" fill="#0F766E" fontSize="3.5" fontWeight="500">&lt;200ms</text>
          <text x="48" y="76" textAnchor="middle" fill="rgba(15, 118, 110, 0.6)" fontSize="3">analytics</text>
          <text x="72" y="14" textAnchor="middle" fill="rgba(15, 118, 110, 0.6)" fontSize="3">rec</text>
        </g>

        {/* Status indicator */}
        <g className="status">
          <circle cx="6" cy="6" r="2.5" fill="#10B981">
            <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
          </circle>
          <text x="12" y="8" fill="#10B981" fontSize="3.5" fontWeight="600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>12K+</text>
        </g>

        {/* Market indicators */}
        <g className="markets" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          <text x="112" y="70" textAnchor="middle" fill="rgba(15, 118, 110, 0.5)" fontSize="3">150+ markets</text>
        </g>
      </svg>
    </div>
  );
}

export default IzmexCircuit;
